import React, { useEffect, useState, useMemo } from 'react';
import LRUCache from '../components/cache.js'; // Or wherever your cache is located
import '../css/damage.css';

const damageCache = new LRUCache(30);

const DamageWrapper = ({ trueStats, enemyStats, mergedAttacks }) => {
  const [damageCalcs, setDamageCalcs] = useState(null);

  const payload = useMemo(() => {
    if (!mergedAttacks || !trueStats || !enemyStats) return null;
    return {
      attacks: mergedAttacks,
      stats: trueStats,
      context: enemyStats,
    };
  }, [mergedAttacks, trueStats, enemyStats]);

  useEffect(() => {
    const fetchDamage = async () => {
      if (!payload) return;

      const cacheKey = JSON.stringify(payload);
      const cached = damageCache.get(cacheKey);

      if (cached) {
        setDamageCalcs(cached);
        return;
      }

      try {
        const res = await fetch('/api/damage/calc', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: cacheKey,
        });

        if (!res.ok) throw new Error('Failed to fetch damage calculations');

        const data = await res.json();
        damageCache.set(cacheKey, data);
        setDamageCalcs(data);
      } catch (err) {
        console.error('Damage calculation failed:', err);
        setDamageCalcs(null);
      }
    };

    fetchDamage();
  }, [payload]);

  if (!damageCalcs) return null;

  return (
    <div className="damageWrapper">
      <div className="damageTitle">
      <h1>Attacks
      <span className="titleInfoIcon" title="Due to the game's damage formula not being publicly known, damage numbers may be slightly off. If you know how the game rounds numbers, please contact Kreeperwide on Discord.">
      ⓘ
      </span>
      </h1>
      </div>
      {Object.entries(damageCalcs).map(([category, abilities]) => (
        <div className="damageCategory" key={category}>
        <h3>{category.charAt(0).toUpperCase() + category.slice(1)} Attacks:</h3>
        {abilities.map((ability, idx) => (
            <div className="abilityBlock" key={idx}>
            <div className="abilityName">
                {ability.name}
            </div>
            <div className="abilityCategories">
              {ability.type?.map((type, i) => (
                <span className="abilityCategory" key={i}>{type}</span>
              ))}
            </div>
            <div className="damageEstimatesBlock">
            {ability.damageEstimates?.length ? ( 
              <ul className="hitList">
                {ability.damageEstimates.map((hit, i) => (
                  <li key={i}>
                    {/* Shield override */}
                    {hit.flag && hit.flag === "Shield" ? (
                      <>
                        {hit.tag}: <b className="hitMultiplier">{hit.amount}%</b>{" "} {hit.damageType ? `of ${hit.damageType}` : null}
                        {hit.shieldStrength != null && (
                          <span className="shield">Shield HP: {hit.shieldStrength}</span>
                        )}
                        {hit.teamMultiplier != null && (
                          <span className="teamShield">Team shield Multiplier: {Number(hit.teamMultiplier.toFixed(4))}%</span>
                        )}
                      </>
                    ) : hit.flag && hit.flag === "Heal" ? (
                      <>
                      {/* Heal override */}
                        {hit.tag}: <b className="hitMultiplier">{hit.amount}%</b>{" "}
                        {hit.damageType ? `of ${hit.damageType}` : null} × {hit.hits}
                        {hit.hits > 1 ? "s" : ""}
                        {hit.healStrength != null && (
                          <span className="heal">
                            Healing done: {hit.healStrength}
                          </span>
                        )}
                        {hit.teamMultiplier != null && (
                          <span className="teamShield">Team Healing Multiplier: {Number(hit.teamMultiplier.toFixed(4))}% {hit.teamLabel}</span>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Normal attacks */}
                        {hit.tag}: <b className="hitMultiplier">{hit.amount}%</b>{" "}
                        {hit.damageType ? `of ${hit.damageType}` : null} × {hit.hits} {Number.isInteger(hit.hits) ? "hit" : ""}
                        {hit.hits > 1 ? "s" : ""}
                        {hit.critless != null && (
                          <span className="critless">Critless: {hit.critless}</span>
                        )}
                        {hit.average != null && (
                          <span className="average">Average: {hit.average.toFixed(2)}</span>
                        )}
                        {hit.onCrit != null && (
                          <span className="onCrit">On Crit: {hit.onCrit}</span>
                        )}
                      </>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="missingAttackWarning">
                This ability can be unlocked through higher promotions
              </p>
            )}
          </div>

            </div>
        ))}
        </div>
      ))}
    </div>
  );
};

export default DamageWrapper;
